import createI18n from '@anm/i18n/helpers/createI18n';
import { ReturnArrayItem } from 'helpers';

const faq = [
  {
    id: 'different_stock' as const,
    question: 'What are the different types of stock libraries in Wave.video?',
    answer: `<p>All of Wave.video users have access to over 2 million images and video clips. Users subscribed to our premium plans have access to a larger number of videos and images as shown on our pricing table above. The actual number may vary over time.</p>
      <p>Wave.video may offer additional video clips and images for purchase from various providers.</p>`
  },
  {
    id: 'remove_watermark' as const,
    question: 'Can I remove the Wave.video watermark from my Video Landing Pages and embedded videos?',
    answer:
      'Absolutely! Wave.video logos are enforced for Free users only. You can remove the Wave.video logo from your embedded videos once you upgrade. The logo on your Video Landing pages will not be shown as long as you maintain your premium subscription.'
  },
  {
    id: 'hosting_charge' as const,
    question: 'How am I going to be charged for video hosting?',
    answer: `<h5>Embedded videos</h5>
    <p>The Free and Creator plans have 5 and 20 embedded videos included, respectively, free of charge. If you need to embed more videos, please upgrade to the Business plan.</p>
    <p>Users subscribed to the Business plan may embed an unlimited number of videos. The first 200 videos are free of charge. You will be charged 20¢ per month for every additional video after that.</p>
    <h5>Bandwidth</h5>
    <p>Each plan also has the following number of watch hours included:</p>
    <ul>
        <li>Free — 10 GB per month</li>
        <li>Creator — 150 GB per month</li>
        <li>Business — 500 GB per month</li>
    </ul>
    <p>After that, you will be charged 1¢ for every additional 1 Gb of traffic.<br>
        <b>Please note:</b> Free plans will not be able to purchase additional bandwidth.</p>
    <p>Read more about pricing for video hosting <a href="https://wave.video/help/articles/3508582-how-much-am-i-going-to-pay-for-hosted-videos" target="_blank">here</a>.
    </p>`
  },
  {
    id: 'videos_create' as const,
    question: 'How many videos can I create?',
    answer:
      'With any plan, you can create an unlimited number of videos. On every plan, you can publish the videos directly to social platforms. With any paid plan, you can also download the videos in the .mp4 format directly to your computer.'
  },
  {
    id: 'editing_limits' as const,
    question: 'Are there different video editing limits for different plans?',
    answer: `<p><b>No matter what plan you have, you may upload videos of any length to host and share with us.</b></p>
    <p>However, <b>video editing</b> is limited by plan:</p>
    <ul>
        <li>Free users may edit videos up to 15 seconds long (great for Social Media stories)</li>
        <li>Creator users may edit videos up to 30 minutes long</li>
        <li>Business users may edit the maximum length supported (currently up to 2 hours and 60fps)</li>
    </ul>`
  },
  {
    id: 'sign_up' as const,
    question: 'How do I sign up?',
    answer: `You can create your account here: <a href="https://wave.video/signup" title="Sign up"> https://wave.video/signup </a>`
  },
  {
    id: 'payment_options' as const,
    question: 'What are the payment options?',
    answer:
      'We accept all major credit and debit cards. All transactions are handled via Stripe, a secure online payment system. We do not accept checks, cash or any other forms of payment except for cards.'
  },
  {
    id: 'switch_advanced_plan' as const,
    question: 'Can I switch to a more advanced plan after I sign up?',
    answer:
      'Sure, you can get a higher plan at any time. We will automatically prorate the charge by  calculating the price difference for you, so you won’t have to purchase the subscription completely anew.'
  },
  {
    id: 'storage_limits' as const,
    question: 'Are there any storage limits?',
    answer: `<p>Each plan has the following amount of storage included:</p>
        <ul>
          <li>Free — 10 GB</li>
          <li>Creator — 30 GB</li>
          <li>Business — 200 GB</li>
        </ul>
        <p>After that, you will be charged 10¢ per month for every additional 1 GB (Business plan).</p>        
        <p><b>Please note</b>: Free and Creator plans will not be able to purchase additional storage.</p>        
        <p>Read more about pricing for video hosting <a href="https://wave.video/help/articles/3508582-how-much-am-i-going-to-pay-for-hosted-videos" target="_blank">here</a>.</p>        
`
  },
  {
    id: 'teams_support' as const,
    question: 'Does Wave.video support teams?',
    answer:
      'Wave.video supports multiple user logins. You may share your login credentials across your organization to access the same videos and collaborate with your team. Organize your videos in folders to make it easier for your team to work together.'
  },
  {
    id: 'tool_for_agencies' as const,
    question: 'Is Wave.video the right video marketing tool for Agencies?',
    answer: `<p>Yes, Wave.video is the perfect video making solution for Agencies. It has everything needed to scale-up video production and better cater to your client’s needs.</p>  
        <ul>
          <li>Powerful and easy-to-use <b>video editing features</b> including effects, transitions, templates, layouts, advanced text editor, and more</li>
          <li><b>Free stock libraries</b> of over 400,000,000 clips, images and royalty-free soundtracks. Plus animated texts, GIFs and stickers</li>
          <li><b>Brand manager feature</b> that allows you to handle all your clients’ brand guidelines with no need to set colors, fonts and logos each time</li> 
          <li><b>Flexible working environment</b> to set seamless collaboration both with your agency’s clients and your team</li>
          <li><b>Additional video marketing and promotional opportunities</b> that you can up-sell to your clients enlarging the range of services the agency provides</li>
        </ul>`
  },
  {
    id: 'captioning' as const,
    question: 'Do I need to pay for captioning?',
    answer:
      'Business level customers get 200 minutes of free captioning each month. You will be charged 5¢ per minute after that.'
  }
];

const i18nFaq = createI18n('wavePricingFaqData')(faq);

export type FaqItem = typeof faq;
export type FaqItemId = ReturnArrayItem<FaqItem>;

export default i18nFaq;
